import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, merge } from 'rxjs';
import { filter, map, mergeMap, skipUntil, switchMap, tap } from 'rxjs/operators';
import { WorkItemPool } from 'common/workitems/workitem-pool';
import { WorkItemGenesysChat } from 'common/workitems/models/work-item-genesys-chat';
import { WorkItemGenesysEmail } from 'common/workitems/models/work-item-genesys-email';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { CommunicationType } from '../models/communication-type.enum';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';
import { closeConversationWorkItem, openConversationWorkItem, removeWorkItemNotification } from '../actions/conversation-work-item.actions';
import { getConversationChangedActions$, getConversationLoadedActions$ } from '../helpers/merged-actions';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { connectToNotificationChannelSuccess } from 'common/store/genesys-cloud/notifications/actions/notification.actions';
import { selectAllConversationIds } from '../selectors/conversation.selectors';
import { difference } from 'lodash';
import { GenesysCloudWorkItemNotificationsService } from 'shared/services/genesys-workitem-notifications/genesys-cloud-workitem-notifications.service';

@Injectable()
export class GenesysCloudConversationWorkItemEffects {
  private readonly initialConversations$ = getConversationLoadedActions$(this.actions$).pipe(
    mergeMap(({ conversations }) => from(conversations))
  );

  private readonly changedConversations$ = getConversationChangedActions$(this.actions$).pipe(
    map(({ conversation }) => conversation)
  );

  private readonly successfulNotificationReconnects$ = this.actions$.pipe(
    ofType(connectToNotificationChannelSuccess),
    filter(({ isReconnect }) => isReconnect)
  );

  readonly registerConversationWorkItems$ = createEffect(() => merge(this.initialConversations$, this.changedConversations$).pipe(
    filter(conversation => !this.workItemPool.getWorkItemById(conversation.id)),
    map(conversation => this.createWorkItemByCommunicationType(conversation.id, conversation.communicationType))
  ), { dispatch: false });

  readonly openConversation$ = createEffect(() => this.actions$.pipe(
    ofType(openConversationWorkItem),
    tap(({ conversationId, focus }) => {
      this.workItemStateHandler.openWorkItem(conversationId);
      if (focus) {
        this.workItemStateHandler.focusWorkItem(conversationId);
      }
    })
  ), { dispatch: false });

  readonly deRegisterConversationWorkItems$ = createEffect(() => getConversationChangedActions$(this.actions$).pipe(
    map(({ conversation }) => conversation),
    filter(conversation => !conversation.isActive),
    map(conversation => closeConversationWorkItem({ conversationId: conversation.id }))
  ));

  readonly deRegisterDisappearedConversations$ = createEffect(() => getConversationLoadedActions$(this.actions$).pipe(
    skipUntil(this.successfulNotificationReconnects$),
    switchMap(() => this.store.select(selectAllConversationIds)),
    mergeMap(allConversationIds => from(difference(this.workItemStateHandler.openedWorkItemIds, allConversationIds))),
    map(conversationId => closeConversationWorkItem({ conversationId }))
  ));

  readonly minimizeWorkItem$ = createEffect(() => this.actions$.pipe(
    ofType(closeConversationWorkItem),
    tap(({ conversationId }) => this.workAreaManager.removeWorkItem(conversationId))
  ), { dispatch: false });

  readonly deRegisterConversationNotification$ = createEffect(() => getConversationChangedActions$(this.actions$).pipe(
    map(({ conversation }) => conversation),
    filter(conversation => !conversation.isActive),
    map( conversation => removeWorkItemNotification( {conversationId: conversation.id}))
  ));

  readonly removeWorkItemNotification$ = createEffect(() => this.actions$.pipe(
    ofType(removeWorkItemNotification),
    tap(({ conversationId }) => this.workItemNotificationService.removeNotificationsForWorkitem(conversationId))
  ), { dispatch: false });

  constructor(private readonly actions$: Actions,
              private readonly store: Store,
              private readonly workItemStateHandler: WorkItemStateHandler,
              private readonly workAreaManager: WorkAreaManager,
              private readonly workItemPool: WorkItemPool,
              private readonly workItemNotificationService: GenesysCloudWorkItemNotificationsService
  ) {
  }

  private createWorkItemByCommunicationType(conversationId: string, communicationType: CommunicationType): WorkItem {
    switch (communicationType) {
      case CommunicationType.Message:
        return new WorkItemGenesysChat(conversationId);
      case CommunicationType.Email:
        return new WorkItemGenesysEmail(conversationId);
      default:
        throw new Error(`Unhandled communication type: ${communicationType}`);
    }
  }
}
